import React from "react";
import Markdown from "markdown-to-jsx";
import { useTheme } from "@mui/material/styles";

const elements = [
	"a",
	"abbr",
	"address",
	"area",
	"article",
	"aside",
	"audio",
	"b",
	"base",
	"bdi",
	"bdo",
	"big",
	"blockquote",
	"body",
	"br",
	"button",
	"canvas",
	"caption",
	"cite",
	"code",
	"col",
	"colgroup",
	"data",
	"datalist",
	"dd",
	"del",
	"details",
	"dfn",
	"dialog",
	"div",
	"dl",
	"dt",
	"em",
	"embed",
	"fieldset",
	"figcaption",
	"figure",
	"footer",
	"form",
	"h1",
	"h2",
	"h3",
	"h4",
	"h5",
	"h6",
	"head",
	"header",
	"hgroup",
	"hr",
	"html",
	"i",
	"iframe",
	"img",
	"input",
	"ins",
	"kbd",
	"keygen",
	"label",
	"legend",
	"li",
	"link",
	"main",
	"map",
	"mark",
	"marquee",
	"menu",
	"menuitem",
	"meta",
	"meter",
	"nav",
	"noscript",
	"object",
	"ol",
	"optgroup",
	"option",
	"output",
	"p",
	"param",
	"picture",
	"pre",
	"progress",
	"q",
	"rp",
	"rt",
	"ruby",
	"s",
	"samp",
	"script",
	"section",
	"select",
	"small",
	"source",
	"span",
	"strong",
	"style",
	"sub",
	"summary",
	"sup",
	"table",
	"tbody",
	"td",
	"textarea",
	"tfoot",
	"th",
	"thead",
	"time",
	"title",
	"tr",
	"track",
	"u",
	"ul",
	"var",
	"video",
	"wbr",

	// SVG
	"circle",
	"clipPath",
	"defs",
	"ellipse",
	"foreignObject",
	"g",
	"image",
	"line",
	"linearGradient",
	"marker",
	"mask",
	"path",
	"pattern",
	"polygon",
	"polyline",
	"radialGradient",
	"rect",
	"stop",
	"svg",
	"text",
	"tspan",
];

const allowedElements = ["a", "b", "strong", "em", "u", "code", "del", "i"];

const CustomLink = ({ children, linkStyle, ...props }) => (
	<a {...props} target="_blank" rel="noopener noreferrer" style={linkStyle}>
		{children}
	</a>
);

const ForwardedLabel = ({ children, forwardedStyle }) => (
	<span style={{
		...forwardedStyle,
		fontSize: "0.85em",
		fontStyle: "italic",
		display: "block",
		marginBottom: "2px",
		borderBottom: "1px solid",
		width: "fit-content"
	}}>
		{children}
	</span>
);

const MarkdownWrapper = ({ children }) => {
	const theme = useTheme();
	let text = children;
	let signatureMeta = null;
	let isForwarded = false;
	const linkStyle = React.useMemo(
		() => ({
			color: theme.palette.mode === "dark" ? "#53bdeb" : "#0b57d0",
			textDecorationColor: theme.palette.mode === "dark" ? "#53bdeb" : "#0b57d0",
			fontWeight: 600,
		}),
		[theme.palette.mode]
	);

	const forwardedStyle = React.useMemo(
		() => ({
			color: theme.palette.mode === "dark" ? "#aebac1" : "#667781",
			borderBottomColor: theme.palette.mode === "dark" ? "#435465" : "#d6dadd",
		}),
		[theme.palette.mode]
	);

	if (text && (text.includes("BEGIN:VCARD") || text.includes("data:image/")))
		text = null;

	if (text && typeof text === "string") {
		if (text.startsWith("\u200B\u200B\u200B")) {
			isForwarded = true;
			text = text.replace("\u200B\u200B\u200B", "").trimStart();
			if (/^\[(image|video|audio|ptt|sticker|archivo)\]$/i.test(text.trim())) {
				text = "";
			}
		}

		const normalizedText = text
			.replace(/^[\u200B-\u200D\u2060\uFEFF\u00A0]+/u, "")
			.trimStart();

		const [firstLine = "", ...otherLines] = normalizedText.split(/\r?\n/);
		const restLines = otherLines.join("\n");
		const trimmedFirstLine = firstLine.trim();

		const multilineSignatureMatch = trimmedFirstLine.match(
			/^(?!https?:\/\/)([\p{L}0-9][^:\n]{0,35})\s*:\s*$/u
		);
		const boldInlineSignatureMatch = trimmedFirstLine.match(
			/^\*([\p{L}0-9][^:\n*]{0,35}):\*\s*(.*)$/u
		);

		if (multilineSignatureMatch && restLines.trim().length > 0) {
			signatureMeta = {
				text: multilineSignatureMatch[1].trim(),
				bold: false,
			};
			text = restLines.trimStart();
		} else if (boldInlineSignatureMatch) {
			const inlineBody = (boldInlineSignatureMatch[2] || "").trim();

			if (inlineBody || restLines.trim().length > 0) {
				signatureMeta = {
					text: boldInlineSignatureMatch[1].trim(),
					bold: true,
				};
				text = [inlineBody, restLines].filter(Boolean).join("\n").trimStart();
			}
		}

		const boldRegex = /\*(.*?)\*/g;
		const italicRegex = /_(.*?)_/g;
		const tildaRegex = /~(.*?)~/g;

		text = text.replace(boldRegex, "<b>$1</b>");
		text = text.replace(italicRegex, "<i>$1</i>");
		text = text.replace(tildaRegex, "<s>$1</s>");
	}

	const options = React.useMemo(() => {
		const markdownOptions = {
			disableParsingRawHTML: false,
			forceInline: true,
			overrides: {
				a: {
					component: (props) => <CustomLink {...props} linkStyle={linkStyle} />,
				},
			},
		};

		elements.forEach(element => {
			if (!allowedElements.includes(element)) {
				markdownOptions.overrides[element] = el => el.children || null;
			}
		});

		return markdownOptions;
	}, [linkStyle]);

	if (!text && !isForwarded) return null;

	if (typeof text === "string") {
		return (
			<>
				{isForwarded && <ForwardedLabel forwardedStyle={forwardedStyle}>Reenviado</ForwardedLabel>}
				{signatureMeta && (
					<span
						style={{
							display: "block",
							fontWeight: signatureMeta.bold ? 700 : 600,
							marginBottom: 1,
							lineHeight: 1.2,
							whiteSpace: "pre-wrap",
						}}
					>
						{`${signatureMeta.text}:`}
					</span>
				)}
				<span style={{ whiteSpace: "pre-wrap", overflowWrap: "anywhere", wordBreak: "normal", lineHeight: 1.25 }}>
					<Markdown options={options}>{text}</Markdown>
				</span>
			</>
		);
	}

	return (
		<>
			{isForwarded && <ForwardedLabel forwardedStyle={forwardedStyle}>Reenviado</ForwardedLabel>}
			{text && <Markdown options={options}>{text}</Markdown>}
		</>
	);
};

export default MarkdownWrapper;
